import { useEffect, useState, type FormEvent } from "react";
import { Pagination } from "../components/Pagination";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { legacySellerUrl, orderPath, PANEL, panelPlace, SECTION_GROUPS, sellerPath, type SellerSection } from "../../shared/sellerPaths";
import { sellerSetup } from "../../shared/onboarding";
import type { MessageKey } from "../i18n/messages";
import { MarketOrderPage } from "./MarketOrderPage";
import { sellerNextStep } from "./orderStep";
import { SectionTabs, SellerShell } from "./SellerShell";
import { actingShop, startActing, stopActing } from "../lib/actAs";
import { adultBornBy, argentinaDate, GRADERS, graderLabel, isSealedCategory, listingPath, MARKET_CONDITIONS, MARKET_LANGUAGES, MAX_PHOTOS, NEW_SELLER_DAYS, NEW_SELLER_SAFE_PAY_ABOVE_ARS, NEW_SELLER_SALES, overCap, PAID_PLANS, PLAN_ENDING_NOTICE_DAYS, PLAN_LABEL, PLAN_PERKS, planWith, SELLER_PLANS, type PaidPlan, type SellerPlan, type SellerReservation } from "../../shared/market";
import { formatArs } from "../../shared/money";
import { parsePriceUsd, usdToArs } from "../../shared/marketPrices";
import { CONDITION_LABEL, FINISH_LABEL, LANGUAGE_LABEL } from "../../shared/variants";
import { useAuth } from "../auth/AuthProvider";
import { useCatalog } from "../lib/CatalogProvider";
import { whatsappUrl } from "../../shared/whatsapp";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import { ApiError, imageUrl } from "../lib/api";
import { isPhotoType } from "../lib/resizeImage";
import { useAsync } from "../lib/useAsync";
import { marketApi, type CatalogCard, type MySeller, type MyListing } from "./api";
import { NewListing, type ShopPlan } from "./NewListing";
import { LimitDialog, noteUpgrade, takeUpgradeSource, UpgradeLink } from "./Showcase";
import { track } from "../lib/analytics";
import { BulkPrices } from "./BulkPrices";
import { ListingImport } from "./ListingImport";
import { cardSpecs, StatusPill, useArs } from "./components";
import { OffersList } from "./OffersList";
import { PlanCompare } from "./PlanCompare";
import { SellerSettings } from "./SellerSettings";
import { StatsTab } from "./StatsTab";
import { OrdersExport } from "./InsightsSection";
import { SellerHome, SetupGuide, type Go } from "./SellerHome";
import { CouponsTab } from "./CouponsTab";
import { CustomersTab } from "./CustomersTab";
import { ShopEmailsTab } from "./ShopEmailsTab";
import { AccountingExport, QuickRepliesCard, ReportsCard, ShopLinkCard } from "./PerformanceSection";
import { useInbox } from "./InboxProvider";
import { ShowcaseOr } from "./Showcase";
import { ShopEditor } from "./ShopEditor";
import { IntegrationsPage, IntegrationsTeaser } from "./IntegrationsPage";
import { ReservationsCard, ReserveDialog } from "./Reservations";
import { useDropdowns } from "../components/useDropdowns";
import type { ListingSignal } from "../../shared/marketStats";
import { filterListings, LISTING_SHOW, ListingFilters, NO_FILTER, type ListingFilter, type ListingShow } from "./ListingFilters";
import { useGame } from "../lib/GameProvider";
import { gameFinishes } from "../../shared/games";

/** A shop under review prepares listings and its settings; the rest waits for the approval. */
const PENDING_SECTIONS: readonly SellerSection[] = ["inicio", "publicaciones", "publicar", "ajustes"];
/** Sections the admin opens from inside a shop it set up (ACTING_ENTRIES and what they cover). */
const ACTING_SECTIONS: readonly SellerSection[] = ["inicio", "publicaciones", "publicar", "tienda", "demanda", "estadisticas", "est_cartas", "est_precios", "ajustes"];
/** Mi tienda › Publicaciones: rows on screen at once. */
const MY_LISTINGS_PAGE = 100;

/** /mi-tienda/* — the shop's own area: its frame, and the section (or one of its orders) the address names. */
export function SellerDashboard() {
  const { t } = useI18n();
  const { inbox } = useInbox();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { auth, me, ready, seller: loaded } = useMySeller();
  const [saved, setSeller] = useState<MySeller | null>(null);
  const seller = saved ?? loaded;
  const place = panelPlace(location.pathname, location.search);
  // Admin › Mercado › "Editar tienda" opens /mi-tienda?como=<shop>: this tab works as that shop from a
  // fresh load, so nothing of the admin's own panel stays on screen.
  const como = params.get("como");
  useEffect(() => {
    if (!como) return;
    startActing(como);
    window.location.replace(PANEL);
  }, [como]);
  const acting = actingShop() !== null;

  if (como) return <Loading />;

  if (!auth.loading && !auth.user) return <Navigate to={`/ingresar?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  // Working as a shop the admin can't open anymore (released, session without the code): a way back.
  if (me.error && acting && me.error instanceof ApiError && (me.error.status === 401 || me.error.status === 403)) return <ActingError admin={!["forbidden", "unauthorized"].includes(me.error.code)} />;
  if (me.error) return <ErrorMessage onRetry={me.reload} />;
  if (!ready) return <Loading />;
  // No shop yet: the page that explains selling (and its button to ask). Turned down: the request again.
  if (!seller) return <Navigate to="/vender" replace />;
  if (seller.status === "rejected") return <Navigate to={APPLY_PATH} replace />;
  if (!place) return <Navigate to={PANEL} replace />;
  const pending = seller.status === "pending";
  if (pending && "section" in place && !PENDING_SECTIONS.includes(place.section)) return <Navigate to={PANEL} replace />;
  if (acting && !("section" in place && ACTING_SECTIONS.includes(place.section))) return <Navigate to={PANEL} replace />;

  const perks = PLAN_PERKS[seller.plan?.name ?? "basic"];
  // Integraciones is VMAX's only (the menu doesn't offer it to other plans).
  if ("section" in place && place.section === "integraciones" && !perks.api) return <Navigate to={PANEL} replace />;
  // EX has coupons but not the customer list: its Clientes opens on the coupons.
  const clientesEntry: SellerSection = !perks.customers && perks.coupons > 0 ? "cupones" : "clientes";
  const go: Go = (to, state) => navigate(sellerPath(to), state ? { state } : undefined);
  const mp = params.get("mp");
  // As the market decides who can sell (payments already come as buyers get them).
  const { modes: sellerModes, payments: usablePayments } = sellerSetup(seller);
  const insights = perks.insights;
  const section = "section" in place ? place.section : null;
  // Estadísticas' sub-tabs share its view (one StatsTab, so the period and what's loaded carry over) and its title.
  const statsView = section === "estadisticas" ? "resumen" : section === "est_cartas" ? "cartas" : section === "est_precios" ? "precios" : null;
  const title = section === null ? null : section === "inicio" ? seller.displayName : statsView ? t("mkt.tab.estadisticas") : t(`mkt.tab.${section === "publicar" ? "publicaciones" : section}` as MessageKey);

  if (seller.status === "paused" || seller.status === "banned")
    return (
      <SellerShell seller={seller} place={place} closed>
        <div className="status stack">
          <h1>{seller.status === "paused" ? t("mkt.pausedTitle") : t("mkt.bannedTitle")}</h1>
          {seller.statusReason && <p>{t("mkt.reason", { reason: seller.statusReason })}</p>}
        </div>
      </SellerShell>
    );

  return (
    <SellerShell seller={seller} place={place} limited={pending} clientes={clientesEntry} acting={acting}>
      {"order" in place ? (
        <MarketOrderPage number={place.order} panel />
      ) : (
        <div className="seller-dashboard stack">
          {title && <h1 className="sp-title">{title}</h1>}
          {mp === "ok" && <p className="notice">{t("mkt.mpConnected")}</p>}
          {mp === "error" && <p className="notice notice-warn">{t("mkt.mpError")}</p>}
          {seller.managed && !seller.managed.active && !acting && <ActivateShop needsBirthDate={seller.managed.needsBirthDate} onDone={setSeller} />}
          {pending ? (
            <>
              {section === "inicio" && <PendingHome seller={seller} onSaved={setSeller} />}
              {(section === "publicaciones" || section === "publicar") && <ListingsTab pending add={section === "publicar" ? "one" : undefined} onAddDone={() => section === "publicar" && navigate(sellerPath("publicaciones"), { replace: true })} />}
              {section === "ajustes" && <SellerSettings seller={seller} onSaved={setSeller} />}
            </>
          ) : (
            <>
              <ShopNotice seller={seller} setupMissing={usablePayments.length === 0 || sellerModes.length === 0} paymentMissing={usablePayments.length === 0} deliveryMissing={sellerModes.length === 0} go={go} />
              {(section === "pedidos" || section === "ofertas") && <SectionTabs sections={SECTION_GROUPS.ventas} current={section} label={t("mkt.tab.ventas")} badge={(s) => (s === "pedidos" ? inbox.sales : inbox.sellerOffers)} />}
              {(section === "clientes" || section === "cupones" || section === "emails") && <SectionTabs sections={SECTION_GROUPS.clientes} current={section} label={t("mkt.tab.clientes")} />}
              {statsView && section && <SectionTabs sections={SECTION_GROUPS.estadisticas} current={section} label={t("mkt.tab.estadisticas")} names={{ estadisticas: "st.tabSummary" }} />}
              {section === "inicio" && <SellerHome seller={seller} go={go} onSaved={setSeller} />}
              {(section === "publicaciones" || section === "publicar") && (
                <ListingsTab
                  plan={seller.plan ?? null}
                  featuring={seller.plan ? { perMonth: seller.plan.featuredPerMonth, left: seller.plan.featuredLeft, newSeller: Boolean(seller.isNew) } : undefined}
                  add={section === "publicar" ? "one" : undefined}
                  onAddDone={() => section === "publicar" && navigate(sellerPath("publicaciones"), { replace: true })}
                />
              )}
              {section === "pedidos" && <SalesTab insights={insights} accounting={perks.performance} />}
              {section === "ofertas" && <OffersList role="seller" featuring={seller.plan && seller.plan.featuredPerMonth > 0 ? { left: seller.plan.featuredLeft, newSeller: Boolean(seller.isNew) } : undefined} />}
              {section === "clientes" && <CustomersTab allowed={perks.customers} />}
              {section === "cupones" && <CouponsTab plan={seller.plan?.name ?? "basic"} handle={seller.handle} />}
              {section === "emails" && <ShopEmailsTab allowed={perks.shopEmailsPerMonth > 0} />}
              {section === "tienda" && <ShopEditor seller={seller} onSaved={setSeller} />}
              {section === "demanda" && <DemandTab allowed={perks.demandAlerts} />}
              {statsView && <StatsTab view={statsView} insights={insights} performance={perks.performance} canFeature={Boolean(seller.plan && seller.plan.featuredLeft > 0 && !seller.isNew)} />}
              {section === "plan" && <PlanCard seller={seller} onSaved={setSeller} />}
              {section === "integraciones" && <IntegrationsPage />}
              {section === "ajustes" && (
                <>
                  <ShopLinkCard handle={seller.handle} />
                  {!acting && (
                    <>
                      <QuickRepliesCard />
                      {!perks.api && <IntegrationsTeaser listings={seller.plan?.liveListings ?? 0} />}
                      <ReportsCard seller={seller} allowed={perks.performance} onSaved={setSeller} />
                      <VerifyCard seller={seller} onSaved={setSeller} />
                      <SupportCard seller={seller} />
                    </>
                  )}
                  <SellerSettings seller={seller} onSaved={setSeller} />
                </>
              )}
            </>
          )}
        </div>
      )}
    </SellerShell>
  );
}

/**
 * The signed-in person's shop (null: none), tagged with whose it is: right after the session loads,
 * the answer for "nobody" is still there, and it mustn't send a seller to /vender.
 */
function useMySeller() {
  const auth = useAuth();
  const id = auth.user?.id ?? null;
  const me = useAsync(async (signal) => ({ id, seller: id ? await marketApi.mySeller(signal) : null }), [id]);
  const current = me.data !== null && me.data.id === id;
  return { auth, me, ready: !auth.loading && !me.loading && current, seller: current ? me.data!.seller : null };
}

/** Inicio while the shop is under review: what happens now, the guide's steps to approve it, and the verification photo. */
function PendingHome({ seller, onSaved }: { seller: MySeller; onSaved: (s: MySeller) => void }) {
  const { t } = useI18n();
  const listings = useAsync((signal) => marketApi.myListings(signal), []);
  return (
    <div className="stack">
      <div className="status stack">
        <h2>{t("mkt.pendingTitle")}</h2>
        <p>{t("mkt.pendingText")}</p>
      </div>
      <SetupGuide seller={seller} listings={listings.data?.length ?? 0} completedSales={0} onSaved={onSaved} />
      <p className="muted">{t("mkt.pendingPrepare")}</p>
      <VerificationBox seller={seller} onSaved={onSaved} />
    </div>
  );
}

/** Where a person without a shop asks for one (and a turned-down one asks again). */
export const APPLY_PATH = "/vender/solicitar";

/** /vender/solicitar — the request to open a shop; anyone who already has one goes to it. */
export function SellerApply() {
  const navigate = useNavigate();
  const { auth, me, ready, seller } = useMySeller();
  if (!auth.loading && !auth.user) return <Navigate to={`/ingresar?next=${encodeURIComponent(APPLY_PATH)}`} replace />;
  if (me.error) return <ErrorMessage onRetry={me.reload} />;
  if (!ready) return <Loading />;
  if (seller && seller.status !== "rejected") return <Navigate to={PANEL} replace />;
  return (
    <div className="container section seller-dashboard">
      <ApplyForm previous={seller} onDone={() => navigate(PANEL)} />
    </div>
  );
}

/** /cuenta/vender?tab=… (before Mi tienda had its own place): the same section there, with the rest of the address. */
export function LegacySellerRedirect() {
  const location = useLocation();
  return <Navigate to={legacySellerUrl(location.search)} state={location.state} replace />;
}

/**
 * One warning at a time, the one that blocks the most first: can't sell (no payment or delivery) >
 * over the plan's cap > plan ending > new seller's limits. The next shows once that one is solved.
 */
function ShopNotice({ seller, setupMissing, paymentMissing, deliveryMissing, go }: { seller: MySeller; setupMissing: boolean; paymentMissing: boolean; deliveryMissing: boolean; go: Go }) {
  const { t } = useI18n();
  const plan = seller.plan;
  if (seller.limited && setupMissing)
    return (
      <p className="notice notice-warn">
        🤝 {t("mkt.limitedSetup")}{" "}
        <button type="button" className="btn btn-link btn-sm" onClick={() => go("ajustes")}>
          {t("mkt.goToSettings")}
        </button>
      </p>
    );
  if (setupMissing)
    return (
      <p className="notice notice-warn">
        {t("mkt.setupToSell", { what: [paymentMissing && t("mkt.setupWhatPayment"), deliveryMissing && t("mkt.setupWhatDelivery")].filter(Boolean).join(t("mkt.andBoth")) })}{" "}
        <button type="button" className="btn btn-link btn-sm" onClick={() => go("ajustes")}>
          {t("mkt.goToSettings")}
        </button>
      </p>
    );
  if (plan && overCap(plan.liveListings, plan.maxListings))
    return (
      <p className="notice notice-warn">
        {t("mkt.plan.over", { live: plan.liveListings, max: plan.maxListings ?? 0 })}{" "}
        <button type="button" className="btn btn-link btn-sm" onClick={() => (noteUpgrade("aviso"), go("plan"))}>
          {t("home.seePlans")}
        </button>
      </p>
    );
  if (plan && plan.name !== "basic" && plan.until !== null && new Date(plan.until).getTime() - Date.now() < PLAN_ENDING_NOTICE_DAYS * 86_400_000)
    return (
      <p className="notice notice-warn">
        {t("home.planEnding", { plan: PLAN_LABEL[plan.name], date: new Date(plan.until).toLocaleDateString("es-AR") })}{" "}
        <button type="button" className="btn btn-link btn-sm" onClick={() => (noteUpgrade("aviso"), go("plan"))}>
          {t("home.seePlans")}
        </button>
      </p>
    );
  if (seller.limited) return <p className="notice notice-warn">🤝 {t("mkt.limitedYou", { days: NEW_SELLER_DAYS, sales: NEW_SELLER_SALES })}</p>;
  if (seller.isNew)
    return (
      <p className="notice notice-warn">
        {t("mkt.newSellerYou", { max: formatArs(NEW_SELLER_SAFE_PAY_ABOVE_ARS), days: NEW_SELLER_DAYS, sales: NEW_SELLER_SALES })}
        {!seller.mp.connected && ` ${t("mkt.newSellerConnectMp")}`}
      </p>
    );
  return null;
}

/** The limit the server says was hit (listing_limit / price_limit carry it). */
const limitOf = (err: ApiError, fallback: number) => {
  const max = (err.details as { max?: unknown } | undefined)?.max;
  return typeof max === "number" ? max : fallback;
};

/** The shop's plan, the three plans side by side, and "Quiero EX" / "Quiero VMAX" (to move up, or to renew one that's ending). */
function PlanCard({ seller, onSaved }: { seller: MySeller; onSaved: (s: MySeller) => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const info = useAsync((signal) => marketApi.info(signal), []);
  const plan = seller.plan;
  if (!plan) return null;
  const over = overCap(plan.liveListings, plan.maxListings);
  // Asking again a day after (like the server); renewing a paid plan that's about to end.
  const askedRecently = plan.requestedAt !== null && Date.now() - new Date(plan.requestedAt).getTime() < 86_400_000;
  const ending = plan.name !== "basic" && plan.until !== null && new Date(plan.until).getTime() - Date.now() < PLAN_ENDING_NOTICE_DAYS * 86_400_000;
  const rank = (p: SellerPlan) => SELLER_PLANS.indexOf(p);
  const canAsk = (p: PaidPlan) => rank(p) > rank(plan.name) || (p === plan.name && ending);
  const ask = async (p: PaidPlan) => {
    setBusy(true);
    try {
      const saved = await marketApi.requestPlan(p);
      track("plan_request", { plan: p, source: takeUpgradeSource() });
      onSaved(saved);
    } finally {
      setBusy(false);
    }
  };
  const subscriptionsOn = Boolean(info.data?.planSubscriptions);
  return (
    <section className="plan-card" aria-label={t("mkt.plan.title")}>
      <p>
        <strong>{t("mkt.plan.current", { plan: PLAN_LABEL[plan.name] })}</strong>{" "}
        {plan.name !== "basic" &&
          (plan.source === "founder" && !plan.until
            ? t("mkt.plan.founder")
            : plan.until
              ? t("mkt.plan.until", { date: new Date(plan.until).toLocaleDateString("es-AR") })
              : "")}
      </p>
      <p className="small">
        {plan.maxListings === null ? t("mkt.plan.listingsNoLimit", { live: plan.liveListings }) : t("mkt.plan.listings", { live: plan.liveListings, max: plan.maxListings })}
      </p>
      {over && <p className="small notice notice-warn">{t("mkt.plan.over", { live: plan.liveListings, max: plan.maxListings ?? 0 })}</p>}
      <PlanCompare current={plan.name} />
      <SubscriptionBox seller={seller} onSaved={onSaved} />
      {/* With subscriptions open, plans are chosen (and paid) on the plans page; before, "Quiero X" asks the owner. */}
      {subscriptionsOn ? (
        !seller.subscription && !(plan.source === "founder" && !plan.until) && (
          <div className="row-actions">
            <UpgradeLink source="mi-plan">{t("plans.see")}</UpgradeLink>
          </div>
        )
      ) : askedRecently ? (
        <p className="small muted">{t("mkt.plan.requested", { plan: PLAN_LABEL[plan.requested ?? "ex"] })}</p>
      ) : (
        <div className="row-actions">
          {PAID_PLANS.filter(canAsk).map((p) => (
            <button key={p} type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => void ask(p)}>
              {p === plan.name ? t("mkt.plan.renew", { plan: PLAN_LABEL[p] }) : t("mkt.plan.ask", { plan: PLAN_LABEL[p] })}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * The seller's Mercado Pago subscription: what it charges and when, finishing it, cancelling it (one
 * click, the plan lasts what was paid), and the wait while Mercado Pago confirms the first charge.
 */
function SubscriptionBox({ seller, onSaved }: { seller: MySeller; onSaved: (s: MySeller) => void }) {
  const { t } = useI18n();
  const ars = useArs();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const sub = seller.subscription ?? null;
  const plan = seller.plan;
  // Back from Mercado Pago: the plan turns on when the first charge is confirmed (the webhook), not by coming back.
  const confirming = params.get("suscripcion") === "1" && plan?.source !== "mp_subscription";
  useEffect(() => {
    if (!confirming) return;
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      const fresh = await marketApi.mySeller().catch(() => null);
      if (fresh) onSaved(fresh);
      if (fresh?.plan?.source === "mp_subscription" || tries >= 12) {
        clearInterval(timer);
        setParams((p) => (p.delete("suscripcion"), p), { replace: true });
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [confirming]); // eslint-disable-line react-hooks/exhaustive-deps
  const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("es-AR") : "—");
  const cancel = async () => {
    if (!window.confirm(t("plans.cancelConfirm", { date: day(plan?.until ?? null) }))) return;
    setBusy(true);
    setError(false);
    try {
      onSaved(await marketApi.cancelSubscription());
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  const cancelButton = (
    <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => void cancel()}>
      {t("plans.cancel")}
    </button>
  );
  return (
    <>
      {confirming && (
        <p className="notice small" role="status">
          {t("plans.confirming")}
        </p>
      )}
      {sub?.status === "authorized" && (
        <p className="small sub-box">
          ✅ {t("plans.subActive", { plan: PLAN_LABEL[sub.plan], price: ars(sub.amountArs) })} {sub.nextPaymentAt && t("plans.nextCharge", { date: day(sub.nextPaymentAt) })} {cancelButton}
        </p>
      )}
      {sub?.status === "pending" && !confirming && (
        <p className="small sub-box">
          {t("plans.subPending", { plan: PLAN_LABEL[sub.plan] })}{" "}
          {sub.url && (
            <a href={sub.url} className="btn btn-outline btn-sm">
              {t("plans.finish")}
            </a>
          )}{" "}
          {cancelButton}
        </p>
      )}
      {sub?.status === "paused" && (
        <p className="small notice notice-warn">
          {t("plans.subPaused", { plan: PLAN_LABEL[sub.plan] })} {cancelButton}
        </p>
      )}
      {!sub && plan?.source === "mp_subscription" && plan.until && <p className="small muted">{t("plans.subEnded", { plan: PLAN_LABEL[plan.name], date: day(plan.until) })}</p>}
      {error && (
        <p className="form-error" role="alert">
          {t("plans.cancelError")}
        </p>
      )}
    </>
  );
}

/** Verificado: send the verification photo and ask for it (plans with priority support are answered first). */
function VerifyCard({ seller, onSaved }: { seller: MySeller; onSaved: (s: MySeller) => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const v = seller.verification;
  if (!v) return null;
  const priority = seller.plan ? PLAN_PERKS[seller.plan.name].prioritySupport : false;
  const run = async (work: () => Promise<MySeller>, onError: string) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await work());
    } catch {
      setError(onError);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="plan-card stack-sm" aria-label={t("mkt.verify.title")}>
      <p>
        <strong>🪪 {t("mkt.verify.title")}</strong> {v.verified ? t("mkt.verify.done") : t("mkt.verify.what")}
      </p>
      {!v.verified &&
        (v.requestedAt ? (
          <p className="small">{t(priority ? "mkt.verify.requestedPriority" : "mkt.verify.requested", { date: new Date(v.requestedAt).toLocaleDateString("es-AR") })}</p>
        ) : (
          <>
            <p className="small muted">{seller.hasVerificationPhoto ? t("mkt.verifyPhotoSent") : t("mkt.verifyPhotoHint")}</p>
            <div className="row-actions">
              <label className="btn btn-outline btn-sm">
                {seller.hasVerificationPhoto ? t("mkt.verifyPhotoChange") : t("mkt.verifyPhotoUpload")}
                <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={busy} onChange={(e) => e.target.files?.[0] && void run(() => marketApi.uploadVerification(e.target.files![0]), t("mkt.verifyPhotoError"))} />
              </label>
              <button type="button" className="btn btn-primary btn-sm" disabled={busy || !seller.hasVerificationPhoto} onClick={() => void run(() => marketApi.requestVerification(), t("error.generic"))}>
                {t("mkt.verify.ask")}
              </button>
            </div>
            {priority && <p className="small">⚡ {t("mkt.verify.priority")}</p>}
          </>
        ))}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/** Priority support (plan perk): straight to the store's WhatsApp. Hidden without a number. */
function SupportCard({ seller }: { seller: MySeller }) {
  const { t, pick } = useI18n();
  const { settings } = useCatalog();
  if (!seller.plan || !PLAN_PERKS[seller.plan.name].prioritySupport) return null;
  const url = whatsappUrl(settings.whatsapp ? pick(settings.whatsapp) : null, t("mkt.support.message", { handle: seller.handle, plan: PLAN_LABEL[seller.plan.name] }));
  if (!url) return null;
  return (
    <section className="plan-card" aria-label={t("mkt.support.title")}>
      <p>
        <strong>⚡ {t("mkt.support.title")}</strong> {t("mkt.support.text")}{" "}
        <a className="btn btn-outline btn-sm" href={url} target="_blank" rel="noopener noreferrer">
          {t("mkt.support.button")}
        </a>
      </p>
    </section>
  );
}

/** What buyers are looking for (plan perk): cards and how many want them, never who or their price. */
function DemandTab({ allowed }: { allowed: boolean }) {
  const { t } = useI18n();
  const ars = useArs();
  const { game } = useGame();
  const board = useAsync((signal) => (allowed ? marketApi.demand(game, signal) : Promise.resolve(null)), [allowed, game]);
  if (!allowed) {
    return (
      <ShowcaseOr kind="demand">
        <section className="plan-card" aria-label={t("mkt.demand.title")}>
          <p>
            <strong>{t("mkt.demand.title")}</strong> {t("mkt.demand.teaser", { plan: PLAN_LABEL[planWith("demandAlerts")] })}
          </p>
        </section>
      </ShowcaseOr>
    );
  }
  if (board.error) return <ErrorMessage onRetry={board.reload} />;
  if (!board.data) return <Loading />;
  return (
    <section className="stack" aria-label={t("mkt.demand.title")}>
      <p className="muted">{t("mkt.demand.intro")}</p>
      {board.data.length === 0 ? (
        <p className="muted">{t("mkt.demand.empty")}</p>
      ) : (
        <ul className="demand-list">
          {board.data.map((d) => (
            <li key={d.productId}>
              <img src={imageUrl(d.image)} alt="" width={48} height={67} loading="lazy" />
              <span className="stack-sm">
                <Link to={`/producto/${d.productSlug}`}>
                  <strong>{d.name}</strong>
                </Link>
                <span className="muted small">{[d.setName, d.cardNumber ? `#${d.cardNumber}` : null].filter(Boolean).join(" · ")}</span>
                <span className="small">
                  {t("mkt.demand.wanting", { count: d.wanting })}
                  {" · "}
                  {d.marketCount > 0 ? t("mkt.demand.market", { count: d.marketCount, price: ars(d.marketMinArs ?? 0) }) : t("mkt.demand.nobody")}
                  {d.mine ? ` · ✓ ${t("mkt.demand.mine")}` : ""}
                  {d.onBoard > 0 && (
                    <>
                      {" · "}
                      <Link to={`/se-busca/${encodeURIComponent(d.productSlug)}`}>🔎 {t("wanted.onBoard", { count: d.onBoard })}</Link>
                    </>
                  )}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Any size: it's brought down before it goes up (resizeImage).
const photoOk = isPhotoType;

/** Under review: the social link and the verification photo, when missing (or to change the photo). */
function VerificationBox({ seller, onSaved }: { seller: MySeller; onSaved: (s: MySeller) => void }) {
  const { t } = useI18n();
  // A company applies with its legal name and CUIT: no photo to ask for.
  const company = seller.kind === "company";
  const [link, setLink] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = async (work: () => Promise<MySeller>, message: string) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(await work());
    } catch (err) {
      setError(err instanceof ApiError && err.code === "blocked" ? t("mkt.blockedApply") : message);
    } finally {
      setBusy(false);
    }
  };
  const upload = (file: File | undefined) => {
    if (!file) return;
    if (!photoOk(file)) return setError(t("mkt.verifyPhotoError"));
    void run(() => marketApi.uploadVerification(file), t("mkt.verifyPhotoError"));
  };
  return (
    <section id="verificacion" className="stack verification-box" aria-label={t("mkt.verifyTitle")}>
      <h2>{t("mkt.verifyTitle")}</h2>
      {!seller.socialUrl && (
        <form
          className="stack-sm"
          onSubmit={(e) => {
            e.preventDefault();
            void run(() => marketApi.saveSeller({ socialUrl: link }), t("mkt.socialUrlError"));
          }}
        >
          <label htmlFor="vb-social">{t("mkt.verifySocialMissing")}</label>
          <input id="vb-social" type="text" inputMode="url" autoCapitalize="none" value={link} onChange={(e) => setLink(e.target.value)} maxLength={300} required placeholder="instagram.com/tu_usuario" />
          <button type="submit" className="btn btn-outline btn-sm" disabled={busy}>
            {t("mkt.verifySave")}
          </button>
        </form>
      )}
      {company ? (
        <p>{t("mkt.companyReview")}</p>
      ) : (
        <>
          <p>{seller.hasVerificationPhoto ? t("mkt.verifyPhotoSent") : t("mkt.verifyPhotoMissing")}</p>
          <div className="field">
            <label htmlFor="vb-photo">{seller.hasVerificationPhoto ? t("mkt.verifyPhotoChange") : t("mkt.verifyPhotoUpload")}</label>
            <input id="vb-photo" type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} />
            <span className="field-hint">{t("mkt.verifyPhotoHint")}</span>
          </div>
        </>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function ApplyForm({ previous, onDone }: { previous: MySeller | null; onDone: (s: MySeller) => void }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ handle: previous?.handle ?? "", displayName: previous?.displayName ?? "", contactPhone: previous?.contactPhone ?? "", socialUrl: previous?.socialUrl ?? "", city: previous?.city ?? "", province: previous?.province ?? "", bio: previous?.bio ?? "", note: "", birthDate: "", legalName: previous?.company?.legalName ?? "", cuit: previous?.company?.cuit ?? "" });
  const [kind, setKind] = useState<"person" | "company">(previous?.kind ?? "person");
  const company = kind === "company";
  const [accept, setAccept] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState<File | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  const bad = (k: string) => errors.includes(k);

  const latestAdultBirth = adultBornBy(argentinaDate(new Date()));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    // Under 18: say so right away (the server checks it too).
    if (!company && form.birthDate && form.birthDate > latestAdultBirth) {
      setErrors(["birthDate_minor"]);
      return;
    }
    // Required of a person (checked here, with our message): a new try after a rejection keeps the photo already sent.
    if (!company && (photo ? !photoOk(photo) : !previous?.hasVerificationPhoto)) {
      setErrors(["verifyPhoto"]);
      return;
    }
    setBusy(true);
    setErrors([]);
    try {
      const applied = await marketApi.apply({ ...form, kind, acceptTerms: accept });
      // The photo goes once the request exists; if it fails, the page under review asks for it again.
      onDone(photo && !company ? await marketApi.uploadVerification(photo).catch(() => applied) : applied);
    } catch (err) {
      const fields = err instanceof ApiError ? ((err.details as { fields?: string[] })?.fields ?? [err.code]) : ["generic"];
      setErrors(fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack apply-form" onSubmit={submit}>
      <h1>{t("mkt.applyTitle")}</h1>
      <p>{t("mkt.applyIntro")}</p>
      <ul className="apply-points">
        <li>{t("mkt.applyPoint1")}</li>
        <li>{t("mkt.applyPoint2")}</li>
        <li>{t("mkt.applyPoint3")}</li>
      </ul>
      {previous?.status === "rejected" && <p className="notice notice-warn">{t("mkt.rejected", { reason: previous.statusReason ?? "" })}</p>}
      <fieldset className="radio-cards">
        <legend>{t("mkt.kind")}</legend>
        {(["person", "company"] as const).map((k) => (
          <label key={k} className={kind === k ? "radio-card is-selected" : "radio-card"}>
            <input type="radio" name="kind" value={k} checked={kind === k} onChange={() => setKind(k)} />
            <span>
              <strong>{t(k === "person" ? "mkt.kindPerson" : "mkt.kindCompany")}</strong>
              <span className="muted small">{t(k === "person" ? "mkt.kindPersonHint" : "mkt.kindCompanyHint")}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {company && (
        <div className="field-row">
          <div className="field">
            <label htmlFor="ap-legal">{t("mkt.legalName")}</label>
            <input id="ap-legal" value={form.legalName} onChange={set("legalName")} maxLength={120} required aria-invalid={bad("legalName")} />
            {bad("legalName") && <span className="field-error">{t("mkt.legalNameError")}</span>}
          </div>
          <div className="field">
            <label htmlFor="ap-cuit">{t("mkt.cuit")}</label>
            <input id="ap-cuit" inputMode="numeric" value={form.cuit} onChange={set("cuit")} maxLength={13} required aria-invalid={bad("cuit")} placeholder="30-12345678-9" />
            {bad("cuit") && <span className="field-error">{t("mkt.cuitError")}</span>}
          </div>
        </div>
      )}
      <div className="field">
        <label htmlFor="ap-handle">{t("mkt.handle")}</label>
        <input id="ap-handle" value={form.handle} onChange={set("handle")} autoCapitalize="none" required aria-invalid={bad("handle") || bad("handle_taken")} />
        <span className="field-hint">{t("mkt.handleHint")}</span>
        {bad("handle") && <span className="field-error">{t("mkt.handleError")}</span>}
        {bad("handle_taken") && <span className="field-error">{t("mkt.handleTaken")}</span>}
      </div>
      <div className="field">
        <label htmlFor="ap-name">{t("mkt.shopName")}</label>
        <input id="ap-name" value={form.displayName} onChange={set("displayName")} maxLength={40} required aria-invalid={bad("displayName")} />
      </div>
      <div className="field">
        <label htmlFor="ap-phone">{t("mkt.contactPhone")}</label>
        <input id="ap-phone" type="tel" value={form.contactPhone} onChange={set("contactPhone")} required aria-invalid={bad("contactPhone")} />
        <span className="field-hint">{t("mkt.contactPhoneHint")}</span>
      </div>
      <div className="field">
        <label htmlFor="ap-social">{t("mkt.socialUrl")}</label>
        <input id="ap-social" type="text" inputMode="url" autoCapitalize="none" value={form.socialUrl} onChange={set("socialUrl")} maxLength={300} aria-invalid={bad("socialUrl")} placeholder="instagram.com/tu_usuario" />
        <span className="field-hint">{t("mkt.socialUrlHint")}</span>
        {bad("socialUrl") && <span className="field-error">{t("mkt.socialUrlError")}</span>}
      </div>
      {!company && (
        <div className="field">
          <label htmlFor="ap-verify">{t("mkt.verifyPhoto")}</label>
          <input id="ap-verify" type="file" accept="image/jpeg,image/png,image/webp" aria-invalid={bad("verifyPhoto")} onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
          <span className="field-hint">{t("mkt.verifyPhotoHint")}</span>
          {bad("verifyPhoto") && <span className="field-error">{photo ? t("mkt.verifyPhotoError") : t("mkt.verifyPhotoMissingShort")}</span>}
        </div>
      )}
      <div className="field-row">
        <div className="field">
          <label htmlFor="ap-city">{t("mkt.city")}</label>
          <input id="ap-city" value={form.city} onChange={set("city")} required aria-invalid={bad("city")} />
        </div>
        <div className="field">
          <label htmlFor="ap-prov">{t("mkt.province")}</label>
          <input id="ap-prov" value={form.province} onChange={set("province")} />
        </div>
      </div>
      {!company && (
        <div className="field">
          <label htmlFor="ap-birth">{t("mkt.birthDate")}</label>
          <input
            id="ap-birth"
            type="date"
            autoComplete="bday"
            min="1900-01-01"
            value={form.birthDate}
            onChange={set("birthDate")}
            required
            aria-invalid={bad("birthDate") || bad("birthDate_minor")}
          />
          <span className="field-hint">{t("mkt.birthDateHint")}</span>
          {bad("birthDate") && <span className="field-error">{t("mkt.birthDateError")}</span>}
          {bad("birthDate_minor") && <span className="field-error">{t("mkt.birthDateMinor")}</span>}
        </div>
      )}
      <div className="field">
        <label htmlFor="ap-bio">{t("mkt.bio")}</label>
        <textarea id="ap-bio" rows={3} maxLength={600} value={form.bio} onChange={set("bio")} />
      </div>
      <div className="field">
        <label htmlFor="ap-note">{t("mkt.applyNote")}</label>
        <textarea id="ap-note" rows={3} maxLength={1000} value={form.note} onChange={set("note")} />
        <span className="field-hint">{t("mkt.applyNoteHint")}</span>
      </div>
      <label className="check">
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} />{" "}
        <span>
          {t("mkt.acceptSellerTerms")} <Link to="/mercado/terminos">{t("mkt.marketTerms")}</Link>
        </span>
      </label>
      {errors.includes("blocked") ? (
        <p className="form-error" role="alert">
          {t("mkt.blockedApply")}
        </p>
      ) : (
        errors.length > 0 && !errors.every((e) => ["handle", "handle_taken", "birthDate", "birthDate_minor", "socialUrl", "verifyPhoto", "legalName", "cuit"].includes(e)) && <p className="form-error">{t("mkt.applyError")}</p>
      )}
      <button type="submit" className="btn btn-primary" disabled={!accept || busy}>
        {t("mkt.applySend")}
      </button>
    </form>
  );
}

function ListingsTab({
  pending = false,
  featuring,
  plan = null,
  add,
  onAddDone,
}: {
  pending?: boolean;
  plan?: ShopPlan | null;
  /** The plan's featured listings: per month, left this month, and whether the seller is still new (can't feature yet). */
  featuring?: { perMonth: number; left: number; newSeller: boolean };
  /** /mi-tienda/publicar: the new-listing form, open. */
  add?: "one";
  /** That form closed (cancelled, or the card is published). */
  onAddDone?: () => void;
}) {
  const { t } = useI18n();
  const listings = useAsync((signal) => marketApi.myListings(signal), []);
  const [featureLeft, setFeatureLeft] = useState(featuring?.left ?? 0);
  // "Publicarla en mi tienda" from a card on "Se busca": the new-listing form opens with that card.
  const state = useLocation().state as { publish?: CatalogCard; add?: "one" | "sheet" } | null;
  const publish = state?.publish ?? null;
  // Inicio's "+ Publicar carta" / "Importar" open the form right away.
  const [adding, setAdding] = useState<"one" | "sheet" | "prices" | "dollar" | null>(publish ? "one" : (add ?? state?.add ?? null));
  // "+ Publicar" while the list is open.
  useEffect(() => {
    if (add) setAdding(add);
  }, [add]);
  const closeNew = () => {
    setAdding(null);
    onAddDone?.();
  };
  const [editing, setEditing] = useState<number | null>(null);
  // "Reservar para un cliente" (plan perk): the shop's reservations, and the units each listing has set aside.
  // Reserving for a customer is the owner's, not the admin's working from inside the shop.
  const canReserve = !pending && plan && !actingShop() ? PLAN_PERKS[plan.name].reservations > 0 : false;
  const reservations = useAsync((signal) => (canReserve ? marketApi.reservations(signal) : Promise.resolve([] as SellerReservation[])), [canReserve]);
  const [reserving, setReserving] = useState<MyListing | null>(null);
  // Inicio's "1 sin stock" opens the list filtered (?f=soldout); the quick search, by name (?q=).
  const [params] = useSearchParams();
  const f = params.get("f");
  const shownBy = (LISTING_SHOW as readonly string[]).includes(f ?? "") ? (f as ListingShow) : null;
  const named = params.get("q") ?? "";
  const fromUrl = (): ListingFilter => ({ ...NO_FILTER, show: shownBy ?? NO_FILTER.show, text: named });
  const [filter, setFilter] = useState<ListingFilter>(fromUrl);
  useEffect(() => setFilter(fromUrl()), [shownBy, named]); // eslint-disable-line react-hooks/exhaustive-deps
  // A big shop's list, a page at a time (the filters and the bulk tools still see them all); a new filter starts over.
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [filter]);
  // "Precio en dólares" (plan perk): today's dólar, to show what a dollar price is in pesos.
  const usdPrices = !pending && plan ? PLAN_PERKS[plan.name].usdPrices : false;
  const info = useAsync((signal) => (usdPrices ? marketApi.info(signal) : Promise.resolve(null)), [usdPrices]);
  const more = useDropdowns<"more">("not all");
  // Each row's line: visits, nobody looking, Se busca, over the market (a shop under review has no live ones).
  const signals = useAsync((signal) => (pending ? Promise.resolve([] as ListingSignal[]) : marketApi.signals(signal)), [pending]);
  const signalOf = new Map((signals.data ?? []).map((s) => [s.id, s]));
  const reloadAll = () => {
    listings.reload();
    reservations.reload();
    signals.reload();
  };
  const heldBy = new Map<number, number>();
  for (const r of reservations.data ?? []) if (r.status === "open" || r.status === "claimed") heldBy.set(r.listingId, (heldBy.get(r.listingId) ?? 0) + r.quantity);
  if (listings.error) return <ErrorMessage onRetry={listings.reload} />;
  if (!listings.data) return <Loading />;
  // Live now, from the list (the plan was read when the panel opened; "Publicar otra" changes it): as the server counts.
  const livePlan = plan ? { ...plan, liveListings: listings.data.filter((l) => l.status === "active").length } : null;
  // The open row stays on screen even when an edit takes it out of the filter.
  const matching = filterListings(listings.data, filter, { reserved: heldBy });
  const pages = Math.max(1, Math.ceil(matching.length / MY_LISTINGS_PAGE));
  const onPage = matching.slice((Math.min(page, pages) - 1) * MY_LISTINGS_PAGE, Math.min(page, pages) * MY_LISTINGS_PAGE);
  const open = editing !== null && !onPage.some((l) => l.id === editing) ? listings.data.find((l) => l.id === editing) : undefined;
  const shown = open ? [open, ...onPage] : onPage;
  return (
    <div className="stack">
      {adding === "one" ? (
        <NewListing
          pending={pending}
          plan={livePlan}
          initialCard={publish}
          onCancel={closeNew}
          onChanged={reloadAll}
          onEdit={(id) => {
            closeNew();
            setEditing(id);
            listings.reload();
          }}
        />
      ) : adding === "sheet" ? (
        <ListingImport pending={pending} onClose={() => setAdding(null)} onImported={reloadAll} />
      ) : adding === "prices" || adding === "dollar" ? (
        <BulkPrices listings={listings.data} dollar={adding === "dollar"} maxPriceArs={plan?.maxPriceArs ?? null} onClose={() => setAdding(null)} onSaved={reloadAll} />
      ) : (
        <div className="btn-row listing-actions" ref={more.rootRef}>
          <button type="button" className="btn btn-primary" onClick={() => setAdding("one")}>
            {t("mkt.newListing")}
          </button>
          {/* One main button; the tools for many at once behind "···". */}
          <div className="more-menu">
            <button type="button" className="btn btn-outline" aria-label={t("lst.more")} title={t("lst.more")} {...more.buttonProps("more")}>
              ···
            </button>
            {more.open === "more" && (
              <div id={more.panelId("more")} className="more-menu-panel">
                <button type="button" className="btn btn-link" onClick={() => (more.close(), setAdding("sheet"))}>
                  📋 {t("imp.open")}
                </button>
                {listings.data.length > 1 && (
                  <button type="button" className="btn btn-link" onClick={() => (more.close(), setAdding("prices"))}>
                    💲 {t("bulk.open")}
                  </button>
                )}
                {listings.data.some((l) => l.priceRate) && (
                  <button type="button" className="btn btn-link" onClick={() => (more.close(), setAdding("dollar"))}>
                    💵 {t("bulk.openDollar")}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {canReserve && <ReservationsCard reservations={reservations.data ?? []} onChanged={reloadAll} />}
      {reserving && <ReserveDialog listing={reserving} onClose={() => setReserving(null)} onCreated={reloadAll} />}
      {listings.data.length === 0 && !adding && <p className="muted">{t("mkt.noListings")}</p>}
      {/* The bulk editor lists them itself. */}
      {adding !== "prices" && adding !== "dollar" && listings.data.length > 1 && <ListingFilters listings={listings.data} value={filter} onChange={setFilter} shown={matching.length} reservations={canReserve} />}
      {adding !== "prices" && adding !== "dollar" && listings.data.length > 0 && shown.length === 0 && <p className="muted">{t("lf.none")}</p>}
      {adding !== "prices" && adding !== "dollar" && (
        <ul className="my-listings">
          {shown.map((l) => (
            <MyListingRow
              key={l.id}
              listing={l}
              pending={pending}
              open={editing === l.id}
              onToggle={() => setEditing(editing === l.id ? null : l.id)}
              onChanged={reloadAll}
              feature={featuring && featuring.perMonth > 0 ? { left: featureLeft, onFeatured: setFeatureLeft, newSeller: featuring.newSeller } : undefined}
              plan={livePlan}
              reserved={heldBy.get(l.id) ?? 0}
              onReserve={canReserve ? () => setReserving(l) : undefined}
              usdRate={usdPrices ? (info.data?.usdArsRate ?? null) : undefined}
              signal={signalOf.get(l.id)}
            />
          ))}
        </ul>
      )}
      {adding !== "prices" && adding !== "dollar" && pages > 1 && (
        <Pagination
          page={Math.min(page, pages)}
          totalPages={pages}
          onPage={(n) => {
            setPage(n);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function MyListingRow({
  listing: l,
  pending,
  open,
  onToggle,
  onChanged,
  feature,
  plan = null,
  reserved = 0,
  onReserve,
  usdRate,
  signal,
}: {
  listing: MyListing;
  pending: boolean;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
  /** Featuring (plan perk): what's left this month. */
  feature?: { left: number; onFeatured: (left: number) => void; newSeller: boolean };
  /** Hitting the plan's cap (or its price cap) opens the upgrade dialog. */
  plan?: ShopPlan | null;
  /** Units set aside for a client right now (off the listing's stock). */
  reserved?: number;
  /** "Reservar para un cliente" (plan perk). */
  onReserve?: () => void;
  /** "Precio en dólares" (plan perk): today's dólar (null: not known now); undefined: the plan hasn't it. */
  usdRate?: number | null;
  /** What its row says: visits, nobody looking, Se busca, over the market. */
  signal?: ListingSignal;
}) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const { game: visitorGame } = useGame();
  const usdFmt = (cents: number) => new Intl.NumberFormat(locale === "es" ? "es-AR" : "en-US", { style: "currency", currency: "USD" }).format(cents / 100);
  const [photos, setPhotos] = useState(l.photos);
  const [price, setPrice] = useState(String(l.priceArs));
  const [usdOn, setUsdOn] = useState(l.priceUsdCents != null);
  const [usd, setUsd] = useState(l.priceUsdCents != null ? (l.priceUsdCents / 100).toFixed(2).replace(".", ",") : "");
  const usdCents = parsePriceUsd(usd);
  const [qty, setQty] = useState(String(l.quantity));
  // What the card is: a sealed product has no condition or finish, only its language.
  const sealed = isSealedCategory(l.category);
  // Its game's finishes (One Piece: plain only); one its game doesn't have goes back to plain on saving.
  const finishes = gameFinishes(l.game ?? visitorGame);
  const [specs, setSpecs] = useState({ condition: l.condition, language: l.language, finish: finishes.includes(l.finish) ? l.finish : "normal" });
  // Graded or not, by whom and what grade: the two go together.
  const [grading, setGrading] = useState({ on: l.grader !== null, grader: l.grader ?? GRADERS[0], grade: l.grade !== null ? String(l.grade) : "" });
  const grade = grading.on ? { grader: grading.grader, grade: Number(grading.grade) } : { grader: null, grade: null };
  const gradeMissing = grading.on && !(grade.grade! >= 1);
  const changedSpecs = {
    ...Object.fromEntries(Object.entries(specs).filter(([k, v]) => v !== l[k as keyof typeof specs])),
    ...(!sealed && (grade.grader !== l.grader || grade.grade !== l.grade) ? grade : {}),
  };
  // An open offer or a client's reservation is on the card as it was listed.
  const specsLocked = l.openOffers > 0 || reserved > 0;
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState<"listing" | "price" | null>(null);
  useEffect(() => setPhotos(l.photos), [l.photos]);
  // Two at most, the one to act on first.
  const chips = [
    signal?.abovePct ? { key: "above", warn: true, text: t("sig.above", { pct: signal.abovePct }) } : null,
    signal?.noViews ? { key: "noViews", warn: true, text: t("sig.noViews") } : null,
    signal && signal.wanting > 0 ? { key: "wanting", warn: false, text: `🔎 ${t("sig.wanting", { count: signal.wanting })}` } : null,
  ]
    .filter((c): c is { key: string; warn: boolean; text: string } => c !== null)
    .slice(0, 2);

  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await fn();
      if (done) setMsg(done);
      onChanged();
    } catch (err) {
      if (!pending && plan && err instanceof ApiError && (err.code === "listing_limit" || err.code === "price_limit")) setLimit(err.code === "listing_limit" ? "listing" : "price");
      setMsg(
        err instanceof ApiError && err.code === "listing_limit"
            ? t("mkt.listingLimit", { max: limitOf(err, 0) })
            : err instanceof ApiError && err.code === "price_limit"
              ? t("mkt.priceLimit", { max: formatArs(limitOf(err, 0)) })
              : err instanceof ApiError && err.code === "feature_new_seller"
                ? t("mkt.feature.newSeller")
                : err instanceof ApiError && err.code === "feature_quota"
                  ? t("mkt.feature.noneLeft")
                  : err instanceof ApiError && err.code === "no_rate"
                    ? t("usd.noRate")
                    : t("error.generic"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="my-listing">
      {limit && plan && <LimitDialog kind={limit} plan={plan.name} live={plan.liveListings} price={Number(price) || l.priceArs} onClose={() => setLimit(null)} onPauseOther={() => (setLimit(null), onToggle())} />}
      <button type="button" className="my-listing-head" aria-expanded={open} onClick={onToggle}>
        <img src={imageUrl(l.catalogImage)} alt="" width={48} height={48} />
        <span>
          <strong>
            {l.name}
            {l.featuredUntil && new Date(l.featuredUntil).getTime() > Date.now() ? ` ⭐` : ""}
          </strong>
          <span className="muted small">
            {cardSpecs(l, locale)} · {l.priceUsdCents != null ? `${usdFmt(l.priceUsdCents)} (${ars(l.priceArs)})` : ars(l.priceArs)} · {t("mkt.available", { count: l.quantity })}
            {signal && signal.views30d > 0 && ` · ${t("sig.views", { count: signal.views30d })}`}
          </span>
          {chips.length > 0 && (
            <span className="row-signals">
              {chips.map((c) => (
                <span key={c.key} className={c.warn ? "pill pill-warn" : "pill"}>
                  {c.text}
                </span>
              ))}
            </span>
          )}
        </span>
        {/* All set aside for a client: not "agotada" (setting the stock back would sell it twice). */}
        {!(l.quantity === 0 && reserved > 0) && <StatusPill status={l.status === "active" ? (l.quantity > 0 ? "live" : "soldout") : "draft"} />}
        {l.openOffers > 0 && <span className="pill">{t("mkt.openOffers", { count: l.openOffers })}</span>}
        {reserved > 0 && <span className="pill">{t("rsv.rowPill", { count: reserved })}</span>}
      </button>
      {open && (
        <div className="my-listing-body stack">
          <div className="photo-strip">
            {photos.map((p, i) => (
              <figure key={p}>
                <img src={imageUrl(p)} alt={t("mkt.photoN", { n: i + 1 })} width={96} height={96} />
                <figcaption>
                  {i > 0 && (
                    <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => act(async () => setPhotos((await marketApi.editPhoto(l.id, i, "cover")).photos))}>
                      {t("mkt.makeCover")}
                    </button>
                  )}
                  <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => act(async () => setPhotos((await marketApi.editPhoto(l.id, i, "delete")).photos))}>
                    {t("mkt.deletePhoto")}
                  </button>
                </figcaption>
              </figure>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label className="photo-add">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  capture="environment"
                  className="sr-only"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) void act(async () => setPhotos((await marketApi.uploadPhoto(l.id, file)).photos));
                  }}
                />
                <span>＋ {t("mkt.addPhoto")}</span>
              </label>
            )}
          </div>
          <p className="muted small">{t("mkt.photosHint")}</p>
          <div className="field-row">
            {!sealed && (
              <div className="field">
                <label htmlFor={`c-${l.id}`}>{t("mkt.f.condition")}</label>
                <select id={`c-${l.id}`} value={specs.condition} disabled={specsLocked} onChange={(e) => setSpecs({ ...specs, condition: e.target.value as MyListing["condition"] })}>
                  {MARKET_CONDITIONS.map((c) => (
                    <option key={c} value={c}>
                      {c} · {CONDITION_LABEL[c][locale]}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="field">
              <label htmlFor={`lg-${l.id}`}>{t("mkt.f.language")}</label>
              <select id={`lg-${l.id}`} value={specs.language} disabled={specsLocked} onChange={(e) => setSpecs({ ...specs, language: e.target.value })}>
                {MARKET_LANGUAGES.map((code) => (
                  <option key={code} value={code}>
                    {LANGUAGE_LABEL[code]?.[locale] ?? code}
                  </option>
                ))}
              </select>
            </div>
            {!sealed && finishes.length > 1 && (
              <div className="field">
                <label htmlFor={`f-${l.id}`}>{t("mkt.f.finish")}</label>
                <select id={`f-${l.id}`} value={specs.finish} disabled={specsLocked} onChange={(e) => setSpecs({ ...specs, finish: e.target.value as MyListing["finish"] })}>
                  {finishes.map((f) => (
                    <option key={f} value={f}>
                      {FINISH_LABEL[f][locale]}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {!sealed && (
            <>
              <label className="check">
                <input type="checkbox" checked={grading.on} disabled={specsLocked} onChange={(e) => setGrading({ ...grading, on: e.target.checked })} /> {t("nl.graded")}
              </label>
              {grading.on && (
                <div className="field-row">
                  <div className="field">
                    <label htmlFor={`gr-${l.id}`}>{t("mkt.grader")}</label>
                    <select id={`gr-${l.id}`} value={grading.grader} disabled={specsLocked} onChange={(e) => setGrading({ ...grading, grader: e.target.value as (typeof GRADERS)[number] })}>
                      {GRADERS.map((g) => (
                        <option key={g} value={g}>
                          {graderLabel(g)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor={`gd-${l.id}`}>{t("mkt.grade")}</label>
                    <input id={`gd-${l.id}`} type="number" min={1} max={10} step={0.5} value={grading.grade} disabled={specsLocked} onChange={(e) => setGrading({ ...grading, grade: e.target.value })} />
                    {gradeMissing && <span className="field-hint">{t("nl.errGrade")}</span>}
                  </div>
                </div>
              )}
            </>
          )}
          {specsLocked && <p className="muted small">{t("mkt.specsLocked")}</p>}
          {usdRate !== undefined && (
            <label className="check">
              <input
                type="checkbox"
                checked={usdOn}
                onChange={(e) => {
                  setUsdOn(e.target.checked);
                  if (e.target.checked && !usd && usdRate) setUsd(((Number(price) || l.priceArs) / usdRate).toFixed(2).replace(".", ","));
                }}
              />{" "}
              {t("usd.toggle")}
            </label>
          )}
          {l.priceUsdCents != null && l.usdHold && usdRate !== undefined && <p className="notice notice-warn small">{t("usd.heldCap")}</p>}
          {l.priceUsdCents != null && usdRate === undefined && !pending && <p className="notice notice-warn small">{t("usd.planEnded")}</p>}
          <div className="field-row">
            {usdOn && usdRate !== undefined ? (
              <div className="field">
                <label htmlFor={`u-${l.id}`}>{t("usd.price")}</label>
                <input id={`u-${l.id}`} inputMode="decimal" value={usd} onChange={(e) => setUsd(e.target.value)} />
                <span className="field-hint">{usdCents && usdRate ? t("usd.today", { ars: ars(usdToArs(usdCents, usdRate)) }) : usdRate ? t("usd.hint") : t("usd.noRate")}</span>
              </div>
            ) : (
              <div className="field">
                <label htmlFor={`p-${l.id}`}>{t("mkt.priceArs")}</label>
                <input id={`p-${l.id}`} type="number" min={1} value={price} onChange={(e) => setPrice(e.target.value)} />
                {l.priceUsdCents != null && <span className="field-hint">{t("usd.offHint")}</span>}
              </div>
            )}
            <div className="field">
              <label htmlFor={`q-${l.id}`}>{t("mkt.quantity")}</label>
              <input id={`q-${l.id}`} type="number" min={0} max={999} value={qty} onChange={(e) => setQty(e.target.value)} />
              {reserved > 0 && <span className="field-hint">{t("rsv.stockHint", { count: reserved })}</span>}
            </div>
            <button
              type="button"
              className="btn btn-outline"
              disabled={busy || gradeMissing || (usdOn && usdRate !== undefined && !usdCents)}
              onClick={() =>
                act(
                  () =>
                    marketApi.updateListing(l.id, {
                      ...(usdOn && usdRate !== undefined
                        ? { priceUsd: usdCents! / 100, quantity: Number(qty) }
                        : l.priceUsdCents != null
                          ? { priceUsd: null, priceArs: Number(price), quantity: Number(qty) }
                          : { priceArs: Number(price), quantity: Number(qty) }),
                      ...changedSpecs,
                    }),
                  t("mkt.saved"),
                )
              }
            >
              {t("mkt.save")}
            </button>
          </div>
          <div className="row-actions">
            {pending ? (
              <span className="muted small">{t("mkt.publishOnApproval")}</span>
            ) : l.status === "active" ? (
              <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => act(() => marketApi.updateListing(l.id, { status: "paused" }))}>
                {t("mkt.pause")}
              </button>
            ) : (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => act(() => marketApi.updateListing(l.id, { status: "active" }), t("mkt.published"))}>
                {t("mkt.publish")}
              </button>
            )}
            {!pending && l.featuredUntil && new Date(l.featuredUntil).getTime() > Date.now() ? (
              <span className="small">⭐ {t("mkt.feature.until", { date: new Date(l.featuredUntil).toLocaleDateString("es-AR") })}</span>
            ) : (
              !pending &&
              l.status === "active" &&
              feature &&
              (feature.newSeller ? (
                <span className="small muted">⭐ {t("mkt.feature.newSeller")}</span>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  disabled={busy || feature.left <= 0}
                  onClick={() => act(async () => feature.onFeatured((await marketApi.featureListing(l.id)).left), t("mkt.feature.done"))}
                >
                  ⭐ {t("mkt.feature.button", { count: feature.left })}
                </button>
              ))
            )}
            {onReserve && l.status === "active" && l.quantity > 0 && (
              <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={onReserve}>
                🤝 {t("rsv.button")}
              </button>
            )}
            <Link to={listingPath(l)} className="btn btn-link btn-sm">
              {t("mkt.view")}
            </Link>
            {signal && signal.wanting > 0 && (
              <Link to={`/se-busca/${encodeURIComponent(l.productSlug)}`} className="btn btn-link btn-sm">
                🔎 {t("sig.seeWanted")}
              </Link>
            )}
            <button
              type="button"
              className="btn btn-link btn-sm danger"
              disabled={busy}
              onClick={() => {
                if (window.confirm(t("mkt.confirmDelete"))) void act(() => marketApi.removeListing(l.id));
              }}
            >
              {t("mkt.delete")}
            </button>
          </div>
          {msg && <p className="small" role="status">{msg}</p>}
        </div>
      )}
    </li>
  );
}

function SalesTab({ insights, accounting }: { insights: boolean; accounting: boolean }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const orders = useAsync((signal) => marketApi.orders("seller", signal), []);
  if (orders.error) return <ErrorMessage onRetry={orders.reload} />;
  if (!orders.data) return <Loading />;
  if (!orders.data.length) return <p className="muted">{t("mkt.noSales")}</p>;
  return (
    <>
      <OrdersExport allowed={insights} />
      {accounting && <AccountingExport />}
      <OrderRows rows={orders.data} ars={ars} locale={locale} role="seller" attention={(o) => sellerNextStep(o) !== "home.next.view"} />
    </>
  );
}

export function OrderRows({ rows, ars, locale, attention, role = "buyer" }: { rows: Awaited<ReturnType<typeof marketApi.orders>>; ars: (n: number) => string; locale: "es" | "en"; attention: (o: Awaited<ReturnType<typeof marketApi.orders>>[number]) => boolean; /** Whose list it is: each side's order page. */ role?: "buyer" | "seller" }) {
  const { t } = useI18n();
  // The shop's sales: what waits on it first (newest first within each), each with its next step as the button.
  const waits = (o: (typeof rows)[number]) => sellerNextStep(o) !== "home.next.view";
  const shown = role === "seller" ? [...rows].sort((a, b) => Number(waits(b)) - Number(waits(a)) || b.createdAt.localeCompare(a.createdAt)) : rows;
  return (
    <ul className="order-list">
      {shown.map((o) => (
        <li key={o.number} className={attention(o) ? "order-row needs-action" : "order-row"}>
          {o.image && <img src={imageUrl(o.image)} alt="" width={40} height={40} />}
          <div>
            <strong>{o.title}</strong>
            <span className="muted small">
              {o.number} · {o.other} · {new Date(o.createdAt).toLocaleDateString(locale === "es" ? "es-AR" : "en-US")}
            </span>
          </div>
          <StatusPill status={o.status} />
          {o.unread && <span className="pill pill-unread">{t("mkt.unread")}</span>}
          <strong>{ars(o.totalArs)}</strong>
          {role === "seller" ? (
            <Link to={orderPath(o.number, role)} className={waits(o) ? "btn btn-outline btn-sm" : "btn btn-link"} aria-label={`${t(sellerNextStep(o))} ${o.number}`}>
              {t(sellerNextStep(o))}
            </Link>
          ) : (
            <Link to={orderPath(o.number, role)} className="btn btn-link">
              {t("account.viewOrder")}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A shop pokefans set up: its owner looks it over and publishes it by accepting the rules (a person without a birth date gives it). */
function ActivateShop({ needsBirthDate, onDone }: { needsBirthDate: boolean; onDone: (s: MySeller) => void }) {
  const { t } = useI18n();
  const [accept, setAccept] = useState(false);
  const [birthDate, setBirthDate] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const bad = (k: string) => errors.includes(k) || undefined;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors([]);
    try {
      onDone(await marketApi.activate(needsBirthDate ? { acceptTerms: accept, birthDate } : { acceptTerms: accept }));
    } catch (err) {
      setErrors(err instanceof ApiError ? ((err.details as { fields?: string[] } | undefined)?.fields ?? ["form"]) : ["form"]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card stack" onSubmit={submit} noValidate>
      <h2>{t("mkt.activate.title")}</h2>
      <p>{t("mkt.activate.body")}</p>
      <p className="muted small">{t("mkt.activate.keep")}</p>
      {needsBirthDate && (
        <div className="field">
          <label htmlFor="act-birth">{t("mkt.birthDate")}</label>
          <input id="act-birth" type="date" autoComplete="bday" min="1900-01-01" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} required aria-invalid={bad("birthDate") || bad("birthDate_minor")} />
          <span className="field-hint">{t("mkt.birthDateHint")}</span>
          {bad("birthDate") && <span className="field-error">{t("mkt.birthDateError")}</span>}
          {bad("birthDate_minor") && <span className="field-error">{t("mkt.birthDateMinor")}</span>}
        </div>
      )}
      <label className="check">
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} aria-invalid={bad("acceptTerms")} />{" "}
        <span>
          {t("mkt.acceptSellerTerms")} <Link to="/mercado/terminos">{t("mkt.marketTerms")}</Link>
        </span>
      </label>
      {errors.length > 0 && !errors.every((e) => e === "birthDate" || e === "birthDate_minor") && (
        <p className="form-error" role="alert">
          {t("mkt.activate.error")}
        </p>
      )}
      <button type="submit" className="btn btn-primary" disabled={busy || !accept}>
        {t("mkt.activate.go")}
      </button>
    </form>
  );
}

/** The tab still works as a shop it can't open now: back to the admin (or home, for anyone else). It stays in that mode until then: nothing goes out as the signed-in account instead. */
function ActingError({ admin }: { admin: boolean }) {
  const { t } = useI18n();
  return (
    <div className="status stack">
      <p className="notice notice-warn">{t("sp.actingLost")}</p>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          stopActing();
          window.location.assign(admin ? "/admin/mercado" : "/");
        }}
      >
        {t("sp.actingExit")}
      </button>
    </div>
  );
}
